import React, { useRef, useEffect } from "react";
import styles from "../../css/shared/Dialog.module.scss";
import sprite from "../../assets/icons/modals-complete.svg";
import closeIcon from "../../assets/icons/buttons.svg";
import { PropTypes } from "prop-types";
import { useOutsideClick } from "../../utils/useOutsideClick";
import {
	DIALOG_ICONS as icons,
	themeColors,
	red,
	orange,
	purple,
	green,
	blue,
	blueGrey,
	yellow,
} from "../../helpers/utils_styles";
import { useKeyboardShortcut } from "../../utils/useKeyboardShortcut";

// CUSTOM DIALOG W/ PINNED MOBILE STYLES
// CUSTOM ICON OPTIONS: "WARN", "ERROR", "SUCCESS", "INFO"

// CUSTOM STYLES FOR THE ICON WRAPPER (ie background around icon)
const WRAP_COLORS = {
	ERROR: themeColors.flat.red,
	WARN: themeColors.flat.red,
	WARN2: themeColors.flat.red,
	WARN3: yellow[100],
	SUCCESS: themeColors.flat.green,
	INFO: themeColors.flat.purple,
	REPORT: themeColors.flat.purple,
	PRINT: themeColors.blueGreys.main,
	SAVE: themeColors.flat.green,
	CHECKMARK: themeColors.flat.purple,
	SIGNATURE: themeColors.flat.blue,
	ALARM: themeColors.flat.red,
	EDIT: themeColors.flat.orange,
	HELP: themeColors.flat.blue,
	CALENDAR: themeColors.greys.chalk,
	CALENDAR_DONE: themeColors.flat.green,
	CALENDAR_MISSED: themeColors.flat.red,
	USER: themeColors.flat.purple,
	CHART: themeColors.flat.violet,
	SETTINGS: themeColors.flat.purple,
	ALERT: themeColors.flat.red,
	SHOW: themeColors.flat.violet,
	IMAGES: themeColors.flat.purple,
	ADMIN: red[200],
	// NEW!!! ICONS
	CANCEL: themeColors.flat.red,
	CHART2: themeColors.flat.orange,
	CLOUD: purple[300],
	CLOUD_DOWNLOAD: purple[300],
	CLOUD_UPLOAD: purple[300],
	DOWNLOAD: blueGrey[300],
	UPLOAD: blueGrey[300],
	FOLDER: blueGrey[300],
	FOLDER_PERSON: blueGrey[300],
	FIRSTAID: red[200],
	DINNER: green[300],
	HELP1: blue[300],
	// NEWEST
	DELETE: red[200],
	DISABLE: yellow[200],
	REMOVE: yellow[200],
};

// CUSTOM STYLES/FILL FOR ICON, BASED OFF ICON TYPE
const ICON_COLORS = {
	ERROR: themeColors.main.red,
	WARN: themeColors.main.red,
	WARN2: themeColors.main.red,
	WARN3: yellow[600],
	SUCCESS: themeColors.main.green,
	INFO: themeColors.main.main,
	REPORT: themeColors.main.main,
	PRINT: themeColors.main.blackBlue,
	SAVE: themeColors.blueGreys.headings,
	CHECKMARK: themeColors.main.main,
	SIGNATURE: themeColors.main.blue,
	ALARM: themeColors.main.red,
	EDIT: themeColors.main.altRed,
	HELP: themeColors.brand.darkBlue,
	CALENDAR: themeColors.main.blackBlue,
	CALENDAR_DONE: themeColors.main.green,
	CALENDAR_MISSED: themeColors.main.red,
	USER: themeColors.main.purple,
	CHART: themeColors.main.violet,
	SETTINGS: themeColors.main.blackBlue,
	ALERT: themeColors.main.red,
	SHOW: themeColors.main.main,
	IMAGES: themeColors.main.main,
	ADMIN: red[600],
	// NEW!!! ICONS
	CANCEL: themeColors.main.red,
	CHART2: orange[600],
	CLOUD: purple[700],
	CLOUD_DOWNLOAD: purple[700],
	CLOUD_UPLOAD: purple[700],
	DOWNLOAD: blueGrey[700],
	UPLOAD: blueGrey[700],
	FOLDER: blueGrey[700],
	FOLDER_PERSON: blueGrey[700],
	FIRSTAID: red[600],
	DINNER: green[700],
	HELP1: blue[700],
	// NEW
	DELETE: red[600],
	DISABLE: yellow[700],
	REMOVE: red[700],
};

const Dialog = ({
	title,
	heading,
	subheading,
	text,
	closeModal,
	icon = "SUCCESS",
	children,
}) => {
	const modalRef = useRef();
	const { isOutside } = useOutsideClick(modalRef);
	const wasEscaped = useKeyboardShortcut(["Escape"]);

	useEffect(() => {
		let isMounted = true;
		if (!isMounted) {
			return;
		}
		if (isOutside || wasEscaped) {
			return closeModal();
		}

		return () => {
			isMounted = false;
		};
	}, [closeModal, isOutside, wasEscaped]);

	return (
		<aside className={styles.Dialog} ref={modalRef}>
			<section className={styles.Dialog_top}>
				<div
					className={styles.Dialog_top_iconWrapper}
					style={{ backgroundColor: WRAP_COLORS[icon] }}
				>
					<svg className={styles.Dialog_top_iconWrapper_icon}>
						<use
							xlinkHref={`${sprite}#icon-${icons[icon]}`}
							style={{ fill: ICON_COLORS[icon] }}
						></use>
					</svg>
				</div>
				<h2
					className={styles.Dialog_top_title}
					style={{ color: ICON_COLORS[icon] }}
				>
					{title}
				</h2>
				<svg className={styles.Dialog_top_closeIcon} onClick={closeModal}>
					<use xlinkHref={`${closeIcon}#icon-clearclose`}></use>
				</svg>
			</section>
			<section className={styles.Dialog_inner}>
				<h2 className={styles.Dialog_inner_heading}>{heading}</h2>
				<h6 className={styles.Dialog_inner_subheading}>{subheading}</h6>
				<p className={styles.Dialog_inner_text}>{text}</p>
			</section>
			<section className={styles.Dialog_bottom}>{children}</section>
		</aside>
	);
};

export default Dialog;

Dialog.defaultProps = {};

Dialog.propTypes = {
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.any,
	]),
	heading: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.any,
	]),
	subheading: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.any,
	]),
	text: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.any,
	]),
	closeModal: PropTypes.func.isRequired,
	children: PropTypes.any,
};
